'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

// Google Consent Mode v2. Analytics defaults to "denied" (set in the root layout,
// before GA loads), so no non-essential cookies are stored until the visitor accepts.
// This banner records the choice in a first-party cookie and updates consent live.
const COOKIE_NAME = 'kettura_consent';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function persist(value: 'granted' | 'denied') {
  document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax`;
}

function updateGtag(value: 'granted' | 'denied') {
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  if (typeof w.gtag === 'function') {
    w.gtag('consent', 'update', { analytics_storage: value });
  }
}

export default function CookieConsent() {
  const t = useTranslations('CookieConsent');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const hasChoice = /(?:^|; )kettura_consent=(granted|denied)/.test(document.cookie);
    if (!hasChoice) setVisible(true);
  }, []);

  const choose = (value: 'granted' | 'denied') => {
    persist(value);
    updateGtag(value);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t('ariaLabel')}
      className="fixed inset-x-0 bottom-0 z-[3000] px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
    >
      <div className="mx-auto max-h-[70dvh] max-w-4xl overflow-y-auto rounded-xl border border-[#9B7B3A]/30 bg-white shadow-xl">
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <p className="text-sm leading-relaxed text-[#5A5A6E]">
            {t.rich('message', {
              privacyLink: (chunks) => (
                <Link href="/privacy" className="font-medium text-[#9B7B3A] hover:text-[#C4A95E] transition-colors">
                  {chunks}
                </Link>
              ),
            })}
          </p>
          <div className="flex shrink-0 flex-wrap gap-3">
            <button
              type="button"
              onClick={() => choose('denied')}
              className="min-h-11 flex-1 sm:flex-none rounded-full border border-[#9B7B3A]/40 px-5 py-2 text-sm font-medium text-[#1A1A2E] transition-colors hover:bg-[#F0EDE5]"
            >
              {t('decline')}
            </button>
            <button
              type="button"
              onClick={() => choose('granted')}
              className="min-h-11 flex-1 sm:flex-none rounded-full bg-[#9B7B3A] px-5 py-2 text-sm font-semibold text-white shadow-md transition-colors hover:bg-[#C4A95E]"
            >
              {t('accept')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
